import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useToast } from '../sectepe-ui/index';
import { workflowApi } from '../services/api';
import { PageHeader, EmptyState, LoadingState, useConfirm } from '../components/ui';
import { Icon } from '../components/icons';
import { datum, fehlerText } from '../vertrieb/format';
import { schritteAus } from '../inhalte/workflowSchritte';
import { WorkflowErgebnisse } from '../inhalte/WorkflowErgebnisse';

/**
 * Gespeicherte KI-Workflows: mehrere Schritte, jeder mit eigenem Prompt und
 * Anbieter, die Ergebnisse fliessen in die naechsten Schritte. Die Vorlagen
 * (Strategie, Serienformate, Blog, E-Mail) lassen sich jederzeit nachladen;
 * vorhandene werden dabei nicht doppelt angelegt.
 */
const WorkflowListPage: React.FC = () => {
  const { push } = useToast();
  const { confirm } = useConfirm();
  const [workflows, setWorkflows] = useState<any[]>([]);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  // Workflow, dessen gespeicherte Ergebnisse aufgeklappt sind.
  const [ergebnisseVon, setErgebnisseVon] = useState<string | null>(null);
  const [laedtVorlagen, setLaedtVorlagen] = useState(false);
  const [suche, setSuche] = useState('');

  const laden = useCallback(async () => {
    try {
      const daten = (await workflowApi.getWorkflows()) ?? [];
      setWorkflows([...daten].sort((a, b) => String(a.name).localeCompare(String(b.name), 'de')));
      setFehler(null);
    } catch (e) {
      setFehler(fehlerText(e, 'Workflows konnten nicht geladen werden'));
    } finally {
      setLaedt(false);
    }
  }, []);

  useEffect(() => { laden(); }, [laden]);

  const vorlagenLaden = async () => {
    setLaedtVorlagen(true);
    try {
      const b = await workflowApi.seedWorkflows();
      push({
        message: b?.created ? `${b.created} Workflow-Vorlagen angelegt.` : 'Alle Vorlagen waren schon vorhanden.',
        severity: b?.created ? 'success' : 'info',
      });
      await laden();
    } catch (e) {
      setFehler(fehlerText(e, 'Vorlagen konnten nicht geladen werden'));
    } finally {
      setLaedtVorlagen(false);
    }
  };

  const loeschen = async (w: any) => {
    const ok = await confirm({
      title: 'Workflow löschen?',
      message: `„${w.name}“ wird entfernt. Vorlagen lassen sich später wieder laden, eigene Workflows nicht.`,
      confirmLabel: 'Löschen',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await workflowApi.deleteWorkflow(w.id);
      push({ message: `„${w.name}“ gelöscht.`, severity: 'info' });
      await laden();
    } catch (e) {
      setFehler(fehlerText(e, 'Löschen fehlgeschlagen'));
    }
  };

  const begriff = suche.trim().toLowerCase();
  const sichtbar = begriff
    ? workflows.filter((w) => `${w.name} ${w.description ?? ''}`.toLowerCase().includes(begriff))
    : workflows;

  return (
    <div className="page-container">
      <PageHeader
        eyebrow="Inhalte & Werkzeuge"
        title="Meine Workflows"
        subtitle="Mehrstufige KI-Abläufe: jeder Schritt hat einen eigenen Prompt, die Ergebnisse fließen in die nächsten Schritte."
        actions={(
          <>
            <button type="button" className="btn btn-secondary" onClick={vorlagenLaden} disabled={laedtVorlagen}>
              <Icon name="refresh" size={14} /> {laedtVorlagen ? 'Lädt…' : 'Vorlagen laden'}
            </button>
            <Link to="/workflows/create" className="btn btn-primary">
              <Icon name="plus" size={14} /> Neuer Workflow
            </Link>
          </>
        )}
      />

      {fehler && <div className="alert alert-error">{fehler}</div>}

      {laedt ? (
        <LoadingState rows={3} label="Workflows werden geladen" />
      ) : workflows.length === 0 ? (
        <EmptyState
          icon="workflow"
          title="Noch keine Workflows"
          description="Die Vorlagen bringen fertige Abläufe für Strategie, Serienformate, Blog und E-Mail mit. Oder einen eigenen Ablauf Schritt für Schritt zusammenstellen."
          action={{ label: 'Vorlagen laden', onClick: vorlagenLaden }}
          secondaryAction={{ label: 'Eigenen Workflow anlegen', to: '/workflows/create' }}
        />
      ) : (
        <>
          {workflows.length > 6 && (
            <div className="toolbar">
              <input type="search" placeholder="Workflows durchsuchen…" aria-label="Workflows durchsuchen" value={suche} onChange={(e) => setSuche(e.target.value)} />
              <span className="toolbar__group toolbar__group--right text-sm text-secondary">{sichtbar.length} von {workflows.length}</span>
            </div>
          )}
          <div className="grid-cards">
            {sichtbar.map((w) => {
              const schritte = schritteAus(w);
              return (
                <article key={w.id} className="card workflow-karte" aria-label={w.name}>
                  <div className="card-header">
                    <h3 className="card-title"><Link to={`/workflows/${w.id}/execute`}>{w.name}</Link></h3>
                    {w.description && <p className="card-description">{w.description}</p>}
                    <div className="text-xs text-secondary">
                      {schritte.length > 0 && <>{schritte.length} {schritte.length === 1 ? 'Schritt' : 'Schritte'} · </>}
                      angelegt {datum(w.createdAt)}
                      {(w._count?.generations ?? 0) > 0 && (
                        <>
                          {' · '}
                          <button type="button" className="btn btn-sm btn-ghost" aria-expanded={ergebnisseVon === w.id} onClick={() => setErgebnisseVon(ergebnisseVon === w.id ? null : w.id)}>
                            {w._count.generations} {w._count.generations === 1 ? 'Ergebnis' : 'Ergebnisse'}
                          </button>
                        </>
                      )}
                    </div>
                    {ergebnisseVon === w.id && <WorkflowErgebnisse workflowId={w.id} />}
                  </div>
                  <div className="card-footer">
                    <Link to={`/workflows/${w.id}/execute`} className="btn btn-primary btn-sm">
                      <Icon name="play" size={12} /> Ausführen
                    </Link>
                    <Link to={`/workflows/${w.id}/edit`} className="btn btn-secondary btn-sm">
                      <Icon name="edit" size={12} /> Bearbeiten
                    </Link>
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => loeschen(w)} aria-label={`${w.name} löschen`} title="Löschen">
                      <Icon name="trash" size={14} />
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default WorkflowListPage;
